/* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */

import { formatIsoWeek } from '../../helpers/utils';

export default class Datepicker {

  private static readonly DATEPICKER_SELECTOR = '.js-datepicker';
  private static readonly DISPLAY_INPUT_SELECTOR = '.js-datepickerInput';
  private static readonly WEEK_INPUT_SELECTOR = '.js-weekpickerInput';
  private static readonly VALUE_INPUT_SELECTOR = '.js-datepickerValue';
  private static readonly CLEAR_DATE_SELECTOR = '.js-clearDatepicker';

  private static readonly DATE_FORMAT = 'yy-mm-dd';
  private static readonly DISPLAY_FORMAT = 'dd.mm.yy';

  private readonly datepickerInput: JQuery<HTMLInputElement>;
  private readonly valueInput: JQuery<HTMLInputElement>;
  private readonly weekInput?: JQuery<HTMLInputElement>;
  private readonly clearDatepicker: JQuery;

  private readonly blockedDays?: Date[];
  private readonly blockedWeeks?: string[];

  constructor(private container: JQuery) {
    this.datepickerInput = container.find(Datepicker.DISPLAY_INPUT_SELECTOR) as JQuery<HTMLInputElement>;
    this.valueInput = container.find(Datepicker.VALUE_INPUT_SELECTOR) as JQuery<HTMLInputElement>;
    this.clearDatepicker = this.container.find(Datepicker.CLEAR_DATE_SELECTOR);

    const options = {
      altField: this.valueInput,
      onSelect: () => this.handleSelect(),
    } as Record<string, any>;

    if (this.valueInput.prop('min')) {
      options.minDate = new Date(this.valueInput.prop('min'));
    }
    if (this.valueInput.prop('max')) {
      options.maxDate = new Date(this.valueInput.prop('max'));
    }

    if (this.datepickerInput.data('blockedDays')) {
      const dateIsoStrings = this.datepickerInput.data('blockedDays') as string[];
      this.blockedDays = dateIsoStrings.sort().map((d) => new Date(d));
      this.blockedWeeks = this.blockedDays.map(formatIsoWeek);
      options.beforeShowDay = (day) => [this.isDayEnabled(day), ''];
    }

    if (this.isWeekPicker()) {
      this.weekInput = $(Datepicker.WEEK_INPUT_SELECTOR);
      let weekNumber: number;
      if (this.valueInput.val()) {
        weekNumber = $.datepicker.iso8601Week(new Date(this.valueInput.val() as string)) as number;
      }

      options.showWeek = true;

      options.onSelect = (_, inst) => {
        const date = this.getDate();
        weekNumber = $.datepicker.iso8601Week(date) as number;

        this.weekInput?.val(`${weekNumber}/${date.getFullYear()}`);
        this.datepickerInput.val($.datepicker.formatDate(Datepicker.DISPLAY_FORMAT, date, inst.settings));
        this.valueInput.val($.datepicker.formatDate(Datepicker.DATE_FORMAT, date, inst.settings));
        this.handleSelect();
      };

      const addHoverEffect = function addHoverEffect(this: JQuery<HTMLTableRowElement>) {
        $(this).find('td a').addClass('ui-state-hover');
      };
      const removeHoverEffect = function removeHoverEffect(this: JQuery<HTMLTableRowElement>) {
        $(this).find('td a').removeClass('ui-state-hover');
      };

      options.beforeShow = () => {
        $('body')
          .on('mouseover', '.ui-datepicker .ui-datepicker-calendar tr', addHoverEffect)
          .on('mouseleave', '.ui-datepicker .ui-datepicker-calendar tr', removeHoverEffect);
      };

      options.onClose = () => {
        $('body')
          .off('mouseover', '.ui-datepicker .ui-datepicker-calendar tr', addHoverEffect)
          .off('mouseleave', '.ui-datepicker .ui-datepicker-calendar tr', removeHoverEffect);
      };

      options.beforeShowDay = (date: Date) => {
        const dayWeekNumber = $.datepicker.iso8601Week(date) as number;
        return [
          this.isDayEnabled(date),
          dayWeekNumber === weekNumber ? 'ui-highlight-row' : '',
        ];
      };

      this.weekInput.on('focus', () => this.datepickerInput.focus());
    }

    this.datepickerInput.datepicker(options);
    this.bindClearDate();

    if (container.data('linked-datepicker')) {
      this.bindLinkedDatepicker($(container.data('linked-datepicker')));
    }
  }

  static init(container: JQuery): void {
    container
      .find(Datepicker.DATEPICKER_SELECTOR)
      .filter((i, element) => !$(element).data('no-init'))
      .map((i, element) => new Datepicker($(element)));
  }

  static initAll(): void {
    Datepicker.setDefaultOptions();
    Datepicker.init($('body'));
  }

  static setDefaultOptions(): void {
    $.datepicker.setDefaults($.datepicker.regional[document.documentElement.lang]);
    $.datepicker.setDefaults({
      dateFormat: Datepicker.DISPLAY_FORMAT,
      altFormat: Datepicker.DATE_FORMAT,
      firstDay: 1,
    });
  }

  private handleSelect(): void {
    if (this.container.data('autosubmit') !== undefined) {
      this.valueInput[0].form?.requestSubmit();
    }
    this.triggerInputChange();
  }

  private bindClearDate(): void {
    this.clearDatepicker.on('click', () => {
      this.datepickerInput.datepicker('setDate', null);
      this.weekInput?.val('');

      if (this.container.closest('form')) {
        this.handleSelect();
      }
    });
  }

  private bindLinkedDatepicker(linkedDatepickerInput: JQuery): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const linkedDatepicker = new Datepicker(linkedDatepickerInput.closest('.js-datepicker'));

    const maxDate = '0D'; // Today
    const minDate = this.getDate();

    this.getInput().datepicker('option', { maxDate });
    linkedDatepickerInput.datepicker('option', { minDate, maxDate });

    this.bindChangeToLinkedDatepicker(linkedDatepickerInput);
  }

  private bindChangeToLinkedDatepicker(dateUntil: JQuery): void {
    this.getInput().on('change', () => {
      dateUntil.prop('disabled', !this.getInput().val());

      if (!dateUntil.val() || this.getDate() > dateUntil.datepicker('getDate')) {
        dateUntil.datepicker('setDate', this.getDate());
      } else if (!this.getInput().val()) {
        dateUntil.datepicker('setDate', null);
      }
      dateUntil.datepicker('option', { minDate: this.getDate(), maxDate: '0D' });
    });
  }

  triggerInputChange(): void {
    this.datepickerInput.trigger('input');
    this.datepickerInput.trigger('change');

    this.valueInput.trigger('input');
    this.valueInput.trigger('change');

    this.datepickerInput[0].dispatchEvent(new Event('input', { bubbles: true }));
    this.datepickerInput[0].dispatchEvent(new Event('change', { bubbles: true }));

    this.valueInput[0].dispatchEvent(new Event('input', { bubbles: true }));
    this.valueInput[0].dispatchEvent(new Event('change', { bubbles: true }));
  }

  getInput(): JQuery {
    return this.datepickerInput;
  }

  getDate(): Date {
    return this.getInput().datepicker('getDate') as Date;
  }

  isWeekPicker(): boolean {
    return Boolean(this.container.data('weekpicker'));
  }

  private isDayEnabled(day: Date): boolean {
    if (!this.blockedDays) {
      return true;
    }
    if (this.isWeekPicker()) {
      return Boolean(!this.blockedWeeks?.some((blockedWeek) => blockedWeek === formatIsoWeek(day)));
    }
    return !this.blockedDays.some((blockedDay) => blockedDay.getTime() === day.getTime());
  }
}
